/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Alert,
} from "@mui/material"
import {
  Public,
  TrendingUp,
  TrendingDown,
  LocationOn,
  Map as MapIcon,
  Refresh,
  ArrowRightAlt,
  CloudOff,
  CloudDone,
} from "@mui/icons-material"
import { BarChart } from "@mui/x-charts/BarChart"
import localforage from "localforage"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

// Tema oscuro/táctico para el War Room
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

const GeografiaElectoral = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [isLoading, setIsLoading] = useState(false)
  const [errorAcceso, setErrorAcceso] = useState(null)

  // Estados de Seguridad y RLS
  const [esGlobal, setEsGlobal] = useState(false)
  const [cobertura, setCobertura] = useState("Cargando...")

  // Filtros de navegación jerárquica
  const [canton, setCanton] = useState("TODOS")
  const [parroquia, setParroquia] = useState("TODAS")
  const [recinto, setRecinto] = useState("TODOS")

  // Listas dinámicas desde la Base de Datos
  const [listaCantones, setListaCantones] = useState([])
  const [listaParroquias, setListaParroquias] = useState([])
  const [listaRecintos, setListaRecintos] = useState([])

  // Datos analíticos
  const [datosTerritorio, setDatosTerritorio] = useState({
    actasProcesadas: 0,
    totalActas: 0,
    votosValidos: 0,
    candidatos: [],
    desgloseSubNiveles: [],
  })

  // ========================================================
  // 1. INICIALIZACIÓN Y CARGA DE COMBOS (SOLO ADMIN)
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  // Función genérica para traer los filtros
  const fetchFiltros = async (tipo, cant = "TODOS", parr = "TODAS") => {
    try {
      const res = await api.post("/ControlElectoral/getFiltrosGeografia", { tipo, canton: cant, parroquia: parr })
      return res.data?.success ? res.data.data : []
    } catch (e) {
      return []
    }
  }

  // Carga inicial de Cantones y Recintos generales (Si el RLS dice que es Global)
  useEffect(() => {
    if (esGlobal) {
      fetchFiltros("CANTONES").then(setListaCantones)
      fetchFiltros("RECINTOS").then(setListaRecintos)
    }
  }, [esGlobal])

  // ========================================================
  // 2. MANEJADORES EN CASCADA
  // ========================================================
  const handleCantonChange = async (e) => {
    const nuevoCanton = e.target.value
    setCanton(nuevoCanton)
    setParroquia("TODAS")
    setRecinto("TODOS")

    if (nuevoCanton === "TODOS") {
      setListaParroquias([])
      fetchFiltros("RECINTOS", "TODOS", "TODAS").then(setListaRecintos)
    } else {
      fetchFiltros("PARROQUIAS", nuevoCanton).then(setListaParroquias)
      fetchFiltros("RECINTOS", nuevoCanton, "TODAS").then(setListaRecintos)
    }
  }

  const handleParroquiaChange = async (e) => {
    const nuevaParr = e.target.value
    setParroquia(nuevaParr)
    setRecinto("TODOS")
    fetchFiltros("RECINTOS", canton, nuevaParr).then(setListaRecintos)
  }

  // ========================================================
  // 3. CARGA DE ESTADÍSTICAS DEL TERRITORIO
  // ========================================================
  useEffect(() => {
    cargarAnalisisTerritorial()
  }, [canton, parroquia, recinto]) // Se dispara automáticamente al cambiar cualquier filtro

  const cargarAnalisisTerritorial = async () => {
    setIsLoading(true)
    setErrorAcceso(null)
    try {
      if (isOffline) throw new Error("Offline")

      const payload = { canton, parroquia, recinto }
      const res = await api.post("/ControlElectoral/getGeografia", payload)

      if (res.data && res.data.success === false) {
        setErrorAcceso(res.data.message)
        return
      }

      const backendData = res.data
      setDatosTerritorio(backendData.data)
      setEsGlobal(backendData.es_global)
      setCobertura(backendData.cobertura)

      await localforage.setItem("geografia_cache", backendData)
    } catch (error) {
      if (error?.response?.status === 403) {
        setErrorAcceso(error.response.data.message || "Usuario no autorizado.")
      } else {
        const cache = await localforage.getItem("geografia_cache")
        if (cache) {
          setDatosTerritorio(cache.data)
          setEsGlobal(cache.es_global)
          setCobertura(cache.cobertura)
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Cálculos matemáticos
  const maxVotos =
    datosTerritorio.candidatos.length > 0
      ? [...datosTerritorio.candidatos].sort((a, b) => b.votos - a.votos)[0]
      : { votos: 0 }
  const segundoVotos =
    datosTerritorio.candidatos.length > 1
      ? [...datosTerritorio.candidatos].sort((a, b) => b.votos - a.votos)[1]
      : { votos: 0 }
  const nuestroCandidato = datosTerritorio.candidatos.length > 0 ? datosTerritorio.candidatos[0] : { votos: 0 }
  const estamosGanando = nuestroCandidato.votos >= maxVotos.votos && nuestroCandidato.votos > 0
  const brechaVotos = estamosGanando
    ? nuestroCandidato.votos - segundoVotos.votos
    : nuestroCandidato.votos - maxVotos.votos

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-3 p-lg-4" style={{ backgroundColor: "#eef2f6", minHeight: "100vh" }}>
        <BackIcon />

        {/* TOPBAR: ESTADO Y TÍTULO */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "flex-end" }}
          mb={3}
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="900" color="primary.main" display="flex" alignItems="center">
              <Public sx={{ fontSize: 40, mr: 1 }} /> Geografía Electoral
            </Typography>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              fontWeight="bold"
              display="flex"
              alignItems="center"
              mt={0.5}
            >
              <LocationOn fontSize="small" sx={{ mr: 0.5 }} /> {cobertura}
            </Typography>
          </Box>
          <Box display="flex" gap={2} alignItems="center">
            {isOffline ? (
              <Chip icon={<CloudOff />} label="MODO OFFLINE" color="error" />
            ) : (
              <Chip icon={<CloudDone />} label="EN LÍNEA" color="success" />
            )}
            <IconButton
              onClick={cargarAnalisisTerritorial}
              disabled={isLoading || isOffline}
              color="primary"
              sx={{ border: "1px solid #196C87", bgcolor: "white" }}
            >
              <Refresh />
            </IconButton>
          </Box>
        </Box>

        {errorAcceso ? (
          <Alert severity="error" variant="filled" sx={{ borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              ACCESO RESTRINGIDO
            </Typography>
            <Typography variant="body1">{errorAcceso}</Typography>
          </Alert>
        ) : (
          <>
            {/* PANEL DE FILTROS EN CASCADA (REALES) */}
            {esGlobal && (
              <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3, borderTop: "4px solid #196C87" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={1} textAlign="center">
                    <MapIcon color="primary" sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      label="1. Cantón"
                      value={canton}
                      onChange={handleCantonChange}
                      size="small"
                      disabled={isOffline}
                    >
                      <MenuItem value="TODOS">-- Todos los Cantones --</MenuItem>
                      {listaCantones.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="2. Parroquia"
                      value={parroquia}
                      onChange={handleParroquiaChange}
                      size="small"
                      disabled={canton === "TODOS" || isOffline}
                    >
                      <MenuItem value="TODAS">-- Todas las Parroquias --</MenuItem>
                      {listaParroquias.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="3. Recinto Electoral"
                      value={recinto}
                      onChange={(e) => setRecinto(e.target.value)}
                      size="small"
                      disabled={isOffline}
                    >
                      <MenuItem value="TODOS">-- Todos los Recintos --</MenuItem>
                      {listaRecintos.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {isLoading ? (
              <Box display="flex" justifyContent="center" my={10}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <Grid container spacing={4}>
                {/* COLUMNA IZQUIERDA: RESUMEN DEL TERRITORIO */}
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={3}
                    sx={{ p: 3, borderRadius: 3, mb: 3, bgcolor: estamosGanando ? "#e8f5e9" : "#ffebee" }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6" fontWeight="bold" color="textPrimary">
                        Estado del Territorio
                      </Typography>
                      {estamosGanando ? (
                        <TrendingUp color="success" fontSize="large" />
                      ) : (
                        <TrendingDown color="error" fontSize="large" />
                      )}
                    </Box>

                    <Typography variant="h3" fontWeight="900" color={estamosGanando ? "success.main" : "error.main"}>
                      {brechaVotos > 0 ? "+" : ""}
                      {brechaVotos.toLocaleString()}
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary" fontWeight="bold" mb={2}>
                      {estamosGanando ? "VOTOS DE VENTAJA" : "VOTOS POR RECUPERAR"}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" color="textSecondary">
                        Actas Procesadas:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {datosTerritorio.actasProcesadas} de {datosTerritorio.totalActas}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="textSecondary">
                        Votos Válidos Escrutados:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {datosTerritorio.votosValidos.toLocaleString()}
                      </Typography>
                    </Box>
                  </Paper>

                  <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: 350 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={2}>
                      Distribución en esta Zona
                    </Typography>
                    {datosTerritorio.candidatos.length > 0 ? (
                      <BarChart
                        dataset={datosTerritorio.candidatos}
                        yAxis={[{ scaleType: "band", dataKey: "cand_nombre" }]}
                        series={[{ dataKey: "votos", color: "#196C87" }]}
                        layout="horizontal"
                        height={280}
                        margin={{ left: 120, right: 20, top: 10, bottom: 20 }}
                      />
                    ) : (
                      <Typography color="textSecondary" textAlign="center" mt={10}>
                        No hay datos suficientes para graficar.
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* COLUMNA DERECHA: DESGLOSE TÁCTICO */}
                <Grid item xs={12} md={8}>
                  <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <LocationOn color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight="bold" color="textPrimary">
                        Desglose Táctico (Análisis Interno)
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" mb={3}>
                      Observe qué {esGlobal && recinto === "TODOS" ? "recintos" : "juntas"} están aportando la mayor
                      cantidad de votos.
                    </Typography>

                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                          <TableRow>
                            <TableCell>
                              <Typography fontWeight="bold">Sub-Zona</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography fontWeight="bold">Actas</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">Nuestros Votos</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">Rival Fuerte</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">Brecha</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography fontWeight="bold">Estatus</Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {datosTerritorio.desgloseSubNiveles.map((fila, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ fontWeight: "500" }}>{fila.zona}</TableCell>
                              <TableCell align="center">{fila.actas}</TableCell>
                              <TableCell align="right" sx={{ color: "#196C87", fontWeight: "bold" }}>
                                {fila.nuestro.toLocaleString()}
                              </TableCell>
                              <TableCell align="right" sx={{ color: "#757575", fontWeight: "bold" }}>
                                {fila.rival.toLocaleString()}
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                  color={fila.diferencia > 0 ? "success.main" : "error.main"}
                                >
                                  <Typography fontWeight="bold" sx={{ mr: 0.5 }}>
                                    {fila.diferencia > 0 ? "+" : ""}
                                    {fila.diferencia.toLocaleString()}
                                  </Typography>
                                  {fila.diferencia > 0 ? (
                                    <ArrowRightAlt sx={{ transform: "rotate(-45deg)" }} />
                                  ) : (
                                    <ArrowRightAlt sx={{ transform: "rotate(45deg)" }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={fila.estado}
                                  size="small"
                                  color={fila.estado === "GANANDO" ? "success" : "error"}
                                  variant="outlined"
                                  sx={{ fontWeight: "bold", borderWidth: 2 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          {datosTerritorio.desgloseSubNiveles.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                <Typography color="textSecondary">
                                  No hay actas ingresadas en esta ubicación aún.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </div>
    </ThemeProvider>
  )
}

export default GeografiaElectoral
